import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUser from "../hooks/useUser";
import UIButton from "../components/Button";
import { storage } from "../utils/storage";
import { TIER_DISPLAY, TierName } from "../types/AIModel";
import { useUserStore, SubscriptionPlatform } from "../store/userStore";
import axios from "axios";

const baseUrl = process.env.REACT_APP_API_CONFIG;

// Platform display names and management URLs
const PLATFORM_INFO: Record<string, { label: string; managementUrl: string; instructions: string }> = {
  apple: {
    label: 'Apple App Store',
    managementUrl: 'https://apps.apple.com/account/subscriptions',
    instructions: 'Open Settings on your iPhone/iPad → Apple ID → Subscriptions',
  },
  google: {
    label: 'Google Play Store',
    managementUrl: 'https://play.google.com/store/account/subscriptions',
    instructions: 'Open Google Play Store → Menu → Subscriptions',
  },
  stripe: {
    label: 'Web (Stripe)',
    managementUrl: '', // Will use API to get portal URL
    instructions: '',
  },
  paddle: {
    label: 'Web (Paddle)',
    managementUrl: '', // Will use API to get portal URL
    instructions: '',
  },
};

interface MenuItemType {
  icon: string;
  text: string;
  path?: string;
  disabled?: boolean;
}

const Profile: React.FC = () => {
  const { userProfile, userEmail, loading, error } = useUser();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);

  // Account deletion (audit C6.3)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionStatus, setDeletionStatus] = useState<{
    pending: boolean;
    scheduled_for?: string;
    grace_days_remaining?: number;
  } | null>(null);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const baseUrlForDelete = process.env.REACT_APP_API_CONFIG || "";

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const axios = (await import("axios")).default;
        const token = storage.getJwtToken();
        if (!token) return;
        const res = await axios.get(`${baseUrlForDelete}/users/me/deletion-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDeletionStatus(res.data);
      } catch {
        /* ignore */
      }
    };
    loadStatus();
  }, [baseUrlForDelete]);

  const handleRequestDeletion = async () => {
    setDeletionLoading(true);
    try {
      const axios = (await import("axios")).default;
      const token = storage.getJwtToken();
      const res = await axios.post(
        `${baseUrlForDelete}/users/me/request-deletion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeletionStatus(res.data);
      setShowDeleteModal(false);
    } catch {
      alert("Could not schedule deletion. Please try again.");
    } finally {
      setDeletionLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setDeletionLoading(true);
    try {
      const axios = (await import("axios")).default;
      const token = storage.getJwtToken();
      await axios.post(
        `${baseUrlForDelete}/users/me/cancel-deletion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeletionStatus({ pending: false });
    } catch {
      alert("Could not cancel deletion. Please try again.");
    } finally {
      setDeletionLoading(false);
    }
  };

  const { subscription, fetchSubscription } = useUserStore();

  useEffect(() => {
    // Fetch subscription details on mount
    fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth >= 768 && window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsTablet(tablet);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Handle manage subscription click
  const handleManageSubscription = async () => {
    if (!subscription?.platform) return;

    if (subscription.platform === 'stripe') {
      // For Stripe, redirect to Customer Portal
      setManagingSubscription(true);
      try {
        const token = storage.getJwtToken();
        const res = await axios.post(
          `${baseUrl}/subscription/stripe/create-portal`,
          { returnUrl: window.location.href },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.data?.url) {
          window.location.href = res.data.data.url;
        }
      } catch (err) {
        console.error('Failed to open subscription portal:', err);
        alert('Failed to open subscription management. Please try again.');
      } finally {
        setManagingSubscription(false);
      }
    } else if (subscription.platform === 'paddle' || subscription.platform === 'lemonsqueezy') {
      // For Paddle/LemonSqueezy, navigate to subscription page
      navigate('/subscription');
    } else {
      // For Apple/Google, open their management URL
      const platformInfo = PLATFORM_INFO[subscription.platform];
      if (platformInfo?.managementUrl) {
        window.open(platformInfo.managementUrl, '_blank');
      }
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleNavigate = (path?: string) => {
    if (!path) return;
    if (path.startsWith("http://") || path.startsWith("https://")) {
      window.open(path, "_blank");
    } else {
      navigate(path);
    }
  };

  const handleLogout = async () => {
    // Revoke refresh token server-side (audit C5) — fire and forget; never
    // block logout on a network failure.
    const refreshToken = storage.getRefreshToken();
    if (refreshToken) {
      try {
        const axios = (await import("axios")).default;
        const baseUrl = process.env.REACT_APP_API_CONFIG || "";
        await axios.post(`${baseUrl}/auth/logout`, { refresh_token: refreshToken });
      } catch {
        /* ignore — local clear below is what actually logs the user out */
      }
    }
    storage.clear();
    navigate("/get-started");
  };

  const menuItems: MenuItemType[] = [
    {
      icon: "/assets/images/icon-apple-profile.svg",
      text: "iOS App",
      path: "",
    },
    {
      icon: "/assets/images/icon-android.svg",
      text: "Android App",
      path: "",
    },
    {
      icon: "/assets/images/icon-chrome.svg",
      text: "Chrome Extension",
      path: "",
    },
    {
      icon: "/assets/images/icon-firefox.svg",
      text: "Firefox Add-on",
      path: "",
    },
    {
      icon: "/assets/images/icon-term-of-use.svg",
      text: "Terms of Use",
      path: "/terms",
    },
    {
      icon: "/assets/images/icon-privacy.svg",
      text: "Privacy Policy",
      path: "https://witworkapp.com/privacy-policy.html",
    },
  ];

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  const upgradeTitle = (
    <h1
      className={`text-white text-xl font-bold mb-4 ${
        isMobile || isTablet ? "text-center" : ""
      }`}>
      Profile
    </h1>
  );

  return (
    <div>
      {(isMobile || isTablet) && (
        <div className="fixed inset-0 bg-black -z-10"></div>
      )}
      <div
        className={`bg-grey p-4 flex lg:w-full lg:gap-8 ${
          isTablet || isMobile
            ? "flex-col z-50 fixed inset-2 rounded-xl overflow-auto"
            : ""
        }`}>
        {(isMobile || isTablet) && (
          <button
            type="button"
            className="absolute top-4 right-4 text-white z-[1000]"
            onClick={() => navigate("/")}
            style={{ cursor: "pointer" }}>
            <img
              src="/assets/images/icon-close.svg"
              alt="Close"
              width="24"
              height="24"
            />
          </button>
        )}

        {(isMobile || isTablet) && upgradeTitle}

        <div
          className={`w-1/2 rounded-xl flex flex-col items-center justify-center gap-6 ${
            isMobile ? "w-full p-4" : ""
          }${isTablet ? "flex w-full items-center p-4" : ""}`}
          style={{
            backgroundImage: "url('/assets/images/bg-recent.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}>
          <img
            src="/assets/images/icon-avatar.svg"
            alt="User Avatar"
            className="w-20 h-20"
          />
          <div className="flex justify-evenly w-full">
            {/* Chat/Conversation Usage */}
            <div className="text-center">
              <h2 className="text-3xl m-0 mb-1 font-bold">
                {userProfile?.conversationsLimit === 'Unlimited' ? (
                  <img
                    src="/assets/images/icon-unlimited.svg"
                    alt="Unlimited"
                    className="w-6 inline"
                  />
                ) : userProfile?.conversationsUsed !== undefined && userProfile?.conversationsLimit !== undefined ? (
                  <span>
                    <span className="text-btn-color">{userProfile.conversationsUsed}</span>
                    <span className="text-white/60 text-xl">/{userProfile.conversationsLimit}</span>
                  </span>
                ) : (
                  "--"
                )}
              </h2>
              <p
                className="text-white font-light uppercase"
                style={{ letterSpacing: 1, fontSize: 11 }}>
                chats <br />
                today
              </p>
            </div>

            {/* Tier Badge - Center */}
            <div
              className="text-center flex flex-col items-center relative"
              style={{ right: -10 }}>
              <div
                onClick={() => navigate("/pricing")}
                style={{ cursor: "pointer" }}
                className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  userProfile?.tier === 'pro'
                    ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                    : userProfile?.tier === 'basic'
                    ? 'bg-gradient-to-br from-blue-500 to-cyan-400'
                    : 'bg-gradient-to-br from-gray-600 to-gray-500'
                }`}
              >
                <span className="text-white text-xs font-bold uppercase">
                  {userProfile?.tier === 'pro' ? 'PRO' : userProfile?.tier === 'basic' ? 'BASIC' : 'FREE'}
                </span>
              </div>
              <p
                className="text-white text-center mt-2 font-light"
                onClick={() => navigate("/pricing")}
                style={{ cursor: "pointer", letterSpacing: 1, fontSize: 11 }}>
                {userProfile?.tier !== 'free' ? 'PREMIUM' : 'FREE'} <br /> ACCOUNT
              </p>
            </div>

            {/* Message Usage */}
            <div className="text-center">
              <h2 className="text-3xl m-0 mb-1 font-bold">
                {userProfile?.chatMessagesLimit === 'Unlimited' ? (
                  <img
                    src="/assets/images/icon-unlimited.svg"
                    alt="Unlimited"
                    className="w-6 inline"
                  />
                ) : userProfile?.chatMessagesUsed !== undefined && userProfile?.chatMessagesLimit !== undefined ? (
                  <span>
                    <span className="text-btn-color">{userProfile.chatMessagesUsed}</span>
                    <span className="text-white/60 text-xl">/{userProfile.chatMessagesLimit}</span>
                  </span>
                ) : (
                  "--"
                )}
              </h2>
              <p
                className="text-white font-light uppercase"
                style={{ letterSpacing: 1, fontSize: 11 }}>
                messages <br />
                today
              </p>
            </div>
          </div>
        </div>
        <div className="lg:w-1/2 lg:mt-10 mt-2 w-full">
          {!isMobile && !isTablet && upgradeTitle}
          <div className="flex flex-col gap-3 my-3">
            <div className=" w-full">
              {/* Account Info */}
              <div className="p-4 rounded-xl bg-light-black">
                <p className="mb-4 flex justify-between items-center">
                  <span style={{ color: "#767A85" }} className="font-bold">
                    Account
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    userProfile?.tier === 'pro'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : userProfile?.tier === 'basic'
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white'
                      : 'bg-gray-600 text-gray-200'
                  }`}>
                    {TIER_DISPLAY[userProfile?.tier as TierName || 'free']?.label || 'FREE'}
                  </span>
                </p>
                <p className="mb-4">
                  <span style={{ color: "#767A85" }} className="font-bold">
                    Email
                  </span>
                  <span style={{ float: "right" }} className="text-white">
                    {userEmail}
                  </span>
                </p>

                {/* Subscription Info - Only show if user has active subscription */}
                {subscription?.hasActiveSubscription && subscription.platform && (
                  <>
                    <div className="border-t border-gray-700 my-4"></div>
                    <p className="mb-4">
                      <span style={{ color: "#767A85" }} className="font-bold">
                        Subscription
                      </span>
                      <span style={{ float: "right" }} className="text-white">
                        {PLATFORM_INFO[subscription.platform]?.label || subscription.platform}
                      </span>
                    </p>
                    <p className="mb-4">
                      <span style={{ color: "#767A85" }} className="font-bold">
                        {subscription.willExpire ? 'Access Until' : 'Renews'}
                      </span>
                      <span style={{ float: "right" }} className="text-white">
                        {formatDate(subscription.expiresAt)}
                      </span>
                    </p>
                    {subscription.willExpire && (
                      <p className="text-yellow-500 text-sm mb-4">
                        Your subscription is canceled and will expire on {formatDate(subscription.expiresAt)}
                      </p>
                    )}

                    {/* Manage Subscription Button */}
                    <button
                      onClick={handleManageSubscription}
                      disabled={managingSubscription}
                      className="w-full mt-2 px-4 py-3 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-all duration-200 font-medium text-sm disabled:opacity-50"
                    >
                      {managingSubscription ? 'Loading...' : 'Manage Subscription'}
                    </button>

                    {/* Instructions for Apple/Google */}
                    {subscription.platform !== 'stripe' && subscription.platform !== 'paddle' && subscription.platform !== 'lemonsqueezy' && (
                      <p className="text-gray-500 text-xs mt-2 text-center">
                        {PLATFORM_INFO[subscription.platform]?.instructions}
                      </p>
                    )}
                  </>
                )}

                {/* Upgrade Button - Only show for free users */}
                {!subscription?.hasActiveSubscription && (
                  <>
                    <div className="border-t border-gray-700 my-4"></div>
                    <button
                      onClick={() => navigate('/subscription')}
                      className="w-full px-4 py-3 rounded-lg btn-color text-black font-bold text-sm hover:opacity-90 transition-all duration-200"
                    >
                      Upgrade to Premium
                    </button>
                  </>
                )}
              </div>
              {/* Options Section */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                  marginTop: "8px",
                }}>
                {menuItems.map((item, index) => {
                  return (
                    <div
                      key={index}
                      style={{
                        minHeight: "104px",
                      }}
                      className={`p-4 rounded-xl flex flex-col justify-between items-start cursor-pointer bg-light-black`}
                      onClick={() => {
                        handleNavigate(item.path);
                      }}>
                      <div
                        style={{
                          gap: "16px",
                        }}
                        className="flex items-center justify-between w-full">
                        <img
                          src={item.icon}
                          alt={item.text}
                          className="w-8 h-8"
                        />
                        <img src="/assets/images/icon-arrow.svg" alt="" />
                      </div>
                      <span className="text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>{" "}
            </div>
          </div>
          <UIButton
            text="Log Out"
            textColor="text-btn-color"
            backgroundColor="btn-disabled"
            classButton="text-base"
            onClick={() => setShowLogoutModal(true)}
          />

          {/* Delete account (audit C6.3) */}
          {deletionStatus?.pending ? (
            <div className="mt-4 p-4 rounded-lg border border-red-500/40 bg-red-500/10">
              <p className="text-red-300 text-sm mb-2">
                Your account is scheduled for deletion in{" "}
                <strong>{deletionStatus.grace_days_remaining ?? 30}</strong> days
                ({deletionStatus.scheduled_for
                  ? new Date(deletionStatus.scheduled_for).toLocaleDateString()
                  : ""}).
              </p>
              <button
                onClick={handleCancelDeletion}
                disabled={deletionLoading}
                className="text-[#3FD4A2] hover:underline text-sm"
              >
                Cancel deletion
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="mt-4 text-red-400 hover:text-red-300 text-sm underline"
            >
              Delete account
            </button>
          )}
        </div>

        {/* Delete account confirmation modal (audit C6.3) */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-light-black p-6 rounded-xl w-96">
              <h3 className="text-white text-xl font-bold mb-4">Delete account?</h3>
              <p className="text-gray-300 mb-2 text-sm">
                Your account will be scheduled for permanent deletion in 30 days.
                You can cancel anytime during that window by logging back in.
              </p>
              <p className="text-gray-300 mb-6 text-sm">
                After 30 days all your data — conversations, uploaded files,
                subscription history — will be permanently removed.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 rounded-lg bg-gray-600 text-white font-bold text-sm"
                  disabled={deletionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestDeletion}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white font-bold text-sm"
                  disabled={deletionLoading}
                >
                  {deletionLoading ? "Scheduling..." : "Schedule deletion"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-light-black p-6 rounded-xl w-96">
              <h3 className="text-white text-xl font-bold mb-4">
                Confirm Logout
              </h3>
              <p className="text-gray-300 mb-6">
                Are you sure you want to logout?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="px-4 py-2 rounded-lg bg-gray-600 text-white hover:bg-opacity-80 transition-all duration-200 font-bold text-sm">
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 rounded-lg btn-color text-black hover:bg-opacity-80 transition-all duration-200 font-bold text-sm">
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
